import { DynamicContent } from '@commblades/commblades';
import { useEffect, useRef, useState } from 'react';
import {
    Accordion, AccordionContent, AccordionHeader, ButtonActionEmphasis, Dropdown, Notification,
} from '@honeycomb-npm/honeycomb-react';

import { DefaultYourFinanceDetail, YourFinanceBladeProps, YourFinanceErrorMessage } from './yourFinanceTypes';
import { useAppDispatch, useAppSelector } from '../../../../stories/hooks';
import AmountDataField from './AmountDataField';
import { YOUR_FINANCE_BLADE_ID } from '../../types';
import { prepareSubmitYourFinance, setSubmitDataReadyState, submitYourFinance } from './yourFinanceSlice';
import { AccordionWrapper } from './yourFinance.styled';
import { getValidationMessage } from '../../../../utils';
import { selectYourFinance, selectYourFinanceSubmit, selectYourFinanceSubmitDataReady } from './yourFinanceSelectors';
import { selectLoanPurposeId, selectSubLoanPurposeId } from '../../../netBank/netBankSelectors';
import { removeExistingBladesAfter } from '../../navigationSlice';
import { HiddenLabelWrapper } from './AmountDataField.styled';

const YourFinance = ({
    isFocusedBlade, bladeId, isCollapseDebt, isFrequencyDrowdownAscent,
}: YourFinanceBladeProps) => {
    const {
        heading,
        description,
        incomeForm,
        financiallySupportForm,
        rentForm,
        repaymentForm,
        creditLimitForm,
        validations,
        accordionHeader,
        infoLabel,
        confirmButtonText,
    } = useAppSelector(selectYourFinance);
    const yourFinanceSubmit = useAppSelector(selectYourFinanceSubmit);
    const selectedLoanPurposeId = useAppSelector(selectLoanPurposeId);
    const selectedSubLoanPurposeId = useAppSelector(selectSubLoanPurposeId);
    const isSubmitReady = useAppSelector(selectYourFinanceSubmitDataReady);

    const bladeID = bladeId || YOUR_FINANCE_BLADE_ID.DEBT_CONSOLIDATION;
    const [yourFinanceDetail, setYourFinanceDetail] = useState(DefaultYourFinanceDetail);
    const [taxType, setTaxType] = useState('BeforeTax');
    const [currentBladeName, SetCurrentBladeName] = useState<'DebtConsolidation'|'BorrowingInput'>('DebtConsolidation');

    const [isError, setIsError] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<YourFinanceErrorMessage>();

    const dispatch = useAppDispatch();
    const ref = useRef<HTMLHeadingElement>(null);
    const incomeAmountRef = useRef<HTMLInputElement>(null);
    const rentAmountRef = useRef<HTMLInputElement>(null);
    const repaymentAmountRef = useRef<HTMLInputElement>(null);
    const creditLimitRef = useRef<HTMLInputElement>(null);

    const handleAmountChanged = (name: string, value: string, recordKey: string) => {
        setYourFinanceDetail((prevFinance) => ({
            ...prevFinance,
            amountData: {
                ...prevFinance.amountData,
                [recordKey]: {
                    ...prevFinance.amountData[recordKey],
                    amount: parseFloat(value === '' ? '0' : value),
                },
            },
        }));
        dispatch(removeExistingBladesAfter(currentBladeName));
    };

    const handleFrequencyChanged = (name: string, value: string, recordKey: string) => {
        setYourFinanceDetail((prevFinance) => ({
            ...prevFinance,
            amountData: {
                ...prevFinance.amountData,
                [recordKey]: {
                    ...prevFinance.amountData[recordKey],
                    frequency: value,
                },
            },
        }));
        dispatch(removeExistingBladesAfter(currentBladeName));
    };

    const handleTaxTypeChanged = (name: string, value: string) => {
        setTaxType(value);
        setYourFinanceDetail((prevFinance) => ({
            ...prevFinance,
            amountData: {
                ...prevFinance.amountData,
                netIncome: {
                    ...prevFinance.amountData.netIncome,
                    beforeTax: value === 'Before tax',
                },
            },
        }));
        dispatch(removeExistingBladesAfter(currentBladeName));
    };

    const handleDependantsChanged = (name: string, value: string) => {
        setYourFinanceDetail((prevFinance) => ({
            ...prevFinance,
            dependants: value,
        }));
        dispatch(removeExistingBladesAfter(currentBladeName));
    };

    const isValidForm = () => {
        const errorMessages: Record<string, string> = {};

        errorMessages.netIncome = getValidationMessage(
            validations.hardValidations.income,
            yourFinanceDetail?.amountData?.netIncome?.amount ?? '',
        );
        errorMessages.rent = getValidationMessage(
            validations.hardValidations.rent,
            yourFinanceDetail?.amountData.rent?.amount ?? '',
        );
        errorMessages.repayment = getValidationMessage(
            validations.hardValidations.repayment,
            yourFinanceDetail?.amountData?.repayment?.amount ?? '',
        );
        errorMessages.creditLimit = getValidationMessage(
            validations.hardValidations.creditLimit,
            yourFinanceDetail?.amountData.creditLimit?.amount ?? '',
        );

        const errors: Record<string, string> = Object.entries(errorMessages)
            .filter(([_, error]) => error)
            .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setIsError(true);

            return false;
        }

        return true;
    };

    const handleNextButton = () => {
        if (isValidForm()) {
            yourFinanceDetail.loanPurposeId = selectedLoanPurposeId;
            yourFinanceDetail.subLoanPurposeId = selectedSubLoanPurposeId;
            dispatch(prepareSubmitYourFinance(yourFinanceDetail));
        }
    };

    const renderRepayment = () => (
        <AmountDataField
            fieldId={`${bladeID}-repayment`}
            recordKey="repayment"
            label={repaymentForm.label}
            inputPlaceHolder="$0"
            helptext={repaymentForm.hintLabel}
            frequencyOptions={[...repaymentForm.dropdownOptions].sort(
                (a, b) => (isFrequencyDrowdownAscent ? Number(a.key) - Number(b.key) : Number(b.key) - Number(a.key)),
            )}
            validation={validations.hardValidations.repayment}
            allowIgnoreAmount
            amountInputRef={repaymentAmountRef}
            defaultDropdownValue="Weekly"
            handleAmountChange={handleAmountChanged}
            handleDropdownChange={handleFrequencyChanged}
        />
    );

    const renderCreditLimit = () => (
        <AmountDataField
            fieldId={`${bladeID}-credit-limit`}
            recordKey="creditLimit"
            label={creditLimitForm.label}
            inputPlaceHolder="$0"
            helptext={creditLimitForm.hintLabel}
            validation={validations.hardValidations.creditLimit}
            allowIgnoreAmount
            amountInputRef={creditLimitRef}
            hideFrequency
            handleAmountChange={handleAmountChanged}
            handleDropdownChange={handleFrequencyChanged}
        />
    );

    useEffect(() => {
        dispatch(setSubmitDataReadyState(false));
        SetCurrentBladeName(bladeID === YOUR_FINANCE_BLADE_ID.DEBT_CONSOLIDATION ? 'DebtConsolidation' : 'BorrowingInput');
        setYourFinanceDetail((prevFinance) => ({
            ...prevFinance,
            bladeName: bladeID,
        }));
        if (financiallySupportForm?.DropdownOptions?.length > 0) {
            setYourFinanceDetail((prevFinance) => ({
                ...prevFinance,
                dependants: financiallySupportForm.DropdownOptions[0].key,
            }));
        }
        if (navigator.userAgent.indexOf('iPhone') > -1) {
            const viewport = document.querySelector('[name=viewport]');

            if (viewport) {
                viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
            }
        }
    }, []);

    useEffect(() => {
        if (isSubmitReady) {
            dispatch(submitYourFinance(yourFinanceSubmit));
            dispatch(setSubmitDataReadyState(false));
        }
    }, [isSubmitReady]);

    useEffect(() => {
        if (isFocusedBlade) {
            ref.current?.focus();
        }
    }, [isFocusedBlade]);

    useEffect(() => {
        if (!isError) return;
        setIsError(false);
        if (fieldErrors?.netIncome) {
            // Need to focus and blur to show the error message due to bug in TextInputHelperCurrency component
            incomeAmountRef.current?.focus();
            incomeAmountRef.current?.blur();
            setTimeout(() => {
                incomeAmountRef.current?.focus();
            }, 0);

            return;
        }
        if (fieldErrors?.rent) {
            // Need to focus and blur to show the error message due to bug in TextInputHelperCurrency component
            rentAmountRef.current?.focus();
            rentAmountRef.current?.blur();
            setTimeout(() => {
                rentAmountRef.current?.focus();
            }, 0);

            return;
        }
        if (fieldErrors?.repayment) {
            // Need to focus and blur to show the error message due to bug in TextInputHelperCurrency component
            repaymentAmountRef.current?.focus();
            repaymentAmountRef.current?.blur();
            setTimeout(() => {
                repaymentAmountRef.current?.focus();
            }, 0);

            return;
        }
        if (fieldErrors?.creditLimit) {
            // Need to focus and blur to show the error message due to bug in TextInputHelperCurrency component
            creditLimitRef.current?.focus();
            creditLimitRef.current?.blur();
            setTimeout(() => {
                creditLimitRef.current?.focus();
            }, 0);
        }
    }, [isError, fieldErrors]);

    return (
        <fieldset>
            <legend>
                <h2 id={`${bladeID}-main-heading`} className="h1" tabIndex={-1} ref={ref}>
                    <DynamicContent content={heading} id={`${bladeID}-main-heading-content`} />
                </h2>
            </legend>
            <DynamicContent
                content={description}
                id={`${bladeID}-main-description`}
                className="mb-3"
                isParagraph
            />
            {incomeForm && (
                <AmountDataField
                    fieldId={`${bladeID}-income`}
                    recordKey="netIncome"
                    label={incomeForm.label}
                    inputPlaceHolder="$0"
                    helptext={incomeForm.hintLabel}
                    frequencyOptions={[...incomeForm.frequencyDropdownOptions].sort(
                        (a, b) => (isFrequencyDrowdownAscent ? Number(a.key) - Number(b.key) : Number(b.key) - Number(a.key)),
                    )}
                    validation={validations.hardValidations.income}
                    amountInputRef={incomeAmountRef}
                    defaultDropdownValue="Monthly"
                    handleAmountChange={handleAmountChanged}
                    handleDropdownChange={handleFrequencyChanged}
                >
                    <Dropdown
                        id={`${bladeID}-income-tax-dropdown`}
                        name={`${bladeID}-income-tax-dropdown`}
                        label={`${bladeID}-income-tax-dropdown`}
                        isValid
                        options={incomeForm.taxDropdownOptions.map((o) => ({ label: o.value, value: o.value }))}
                        value={taxType}
                        onChange={handleTaxTypeChanged}
                        hideDefaultOption
                        aria-label="income tax dropdown"
                    />
                </AmountDataField>
            )}
            {financiallySupportForm && (
                <>
                    <h3 id={`${bladeID}-dependants`} className="mt-1 mb-1">
                        <DynamicContent content={financiallySupportForm.label} id={`${bladeID}-dependants-content`} />
                    </h3>
                    <HiddenLabelWrapper className="row ml-0">
                        <div className="col-md-4 p-0 pr-5 col-12">
                            <Dropdown
                                defaultOption="Select"
                                id={`${bladeID}-dependants-frequency-dropdown`}
                                aria-labelledby={`${bladeID}-dependants`}
                                name={`${bladeID}-dependants-frequency-dropdown`}
                                label=""
                                isValid
                                options={financiallySupportForm.DropdownOptions.map((o) => ({ label: o.value, value: o.key }))}
                                value={yourFinanceDetail.dependants}
                                onChange={handleDependantsChanged}
                                hideDefaultOption
                                required
                            />
                        </div>
                    </HiddenLabelWrapper>
                </>
            )}
            {rentForm && (
                <AmountDataField
                    fieldId={`${bladeID}-rent`}
                    recordKey="rent"
                    label={rentForm.label}
                    inputPlaceHolder="$0"
                    helptext={rentForm.hintLabel}
                    frequencyOptions={[...rentForm.DropdownOptions].sort(
                        (a, b) => (isFrequencyDrowdownAscent ? Number(a.key) - Number(b.key) : Number(b.key) - Number(a.key)),
                    )}
                    validation={validations.hardValidations.rent}
                    allowIgnoreAmount
                    amountInputRef={rentAmountRef}
                    defaultDropdownValue="Weekly"
                    handleAmountChange={handleAmountChanged}
                    handleDropdownChange={handleFrequencyChanged}
                />
            )}
            {isCollapseDebt && (
                <Notification
                    animation={false}
                    heading=""
                    isOpen
                    type="info"
                    isAutoFocus={false}
                >
                    <DynamicContent
                        id={`${bladeID}-debt-notification`}
                        content={infoLabel || 'We can give you a more accurate estimate of your borrowing power if you provide debt details.'}
                    />
                </Notification>
            )}
            {isCollapseDebt && (
                <AccordionWrapper>
                    <Accordion collapseAll isAccordion>
                        <AccordionHeader id={`${bladeID}-debt-accordion-header`} targetContentId={`${bladeID}-debt-accordion-content`}>
                            {accordionHeader || 'I have other existing debt'}
                        </AccordionHeader>
                        <AccordionContent id={`${bladeID}-debt-accordion-content`} targetHeaderId={`${bladeID}-debt-accordion-header`}>
                            {repaymentForm && renderRepayment()}
                            {creditLimitForm && renderCreditLimit()}
                        </AccordionContent>
                    </Accordion>
                </AccordionWrapper>
            )}
            {!isCollapseDebt && repaymentForm && renderRepayment()}
            {!isCollapseDebt && creditLimitForm && renderCreditLimit()}
            <ButtonActionEmphasis
                className="xs-12"
                id={`${bladeID}-estimate-button`}
                data-testid={`${bladeID}-estimate-button`}
                variant="emphasis"
                onClick={handleNextButton}
            >
                {confirmButtonText}
            </ButtonActionEmphasis>
        </fieldset>
    );
};

export default YourFinance;
